import type { TFunction } from 'i18next';
import type { AvailabilityProfile, AvailabilityImportPreviewResponse } from '@ooh/contracts';
import { formatDisplayDate } from '@ooh/ui/lib/date-format';

/** Extracted out of `preview-phase.tsx` (with `resolveCarryOverSupplierName`
 * and `resolveLastImportedAtLabel` below) to keep that file under the
 * repo's per-file line-count budget. */
export interface ConfirmGatingReasons {
  allRowsUnmatched: boolean;
  globalRangeIncomplete: boolean;
  periodLabelIncomplete: boolean;
  supplierIncomplete: boolean;
  monthPeriodIncomplete: boolean;
  dateRangeIncomplete: boolean;
}

/** Picks the first applicable required-field reason, extracted to keep
 * `PreviewPhaseEditor` under the repo's per-function complexity budget.
 * Every upstream, directly-actionable missing-field reason is checked
 * BEFORE `allRowsUnmatched`, which is checked LAST as a catch-all —
 * `allRowsUnmatched` is a downstream symptom that can be caused by any of
 * those same upstream gaps (most commonly a not-yet-picked supplier: of
 * course nothing matches when there's no supplier to match against), so
 * telling the operator to "fix the mapping" ahead of a more specific,
 * actionable reason like "pick a supplier" is confusing (CU-86cbafj4j). */
export function resolveDisabledReason(
  t: TFunction<'availabilityImport'>,
  reasons: ConfirmGatingReasons,
): string | undefined {
  if (reasons.globalRangeIncomplete) return t('preview.disabledGlobalRangeReason');
  if (reasons.periodLabelIncomplete) return t('preview.disabledPeriodLabelReason');
  if (reasons.supplierIncomplete) return t('preview.disabledSupplierReason');
  if (reasons.monthPeriodIncomplete) return t('preview.disabledMonthPeriodReason');
  if (reasons.dateRangeIncomplete) return t('preview.disabledDateRangeReason');
  if (reasons.allRowsUnmatched) return t('preview.disabledUnmatchedMappingReason');
  return undefined;
}

/** Whether any required-field gate blocks Confirm, independent of the
 * in-flight mutation state — extracted alongside `resolveDisabledReason` to
 * keep `PreviewPhaseEditor` under the repo's per-function complexity budget.
 * Order doesn't matter here (pure OR), but mirrors `resolveDisabledReason`'s
 * priority order for readability. */
export function hasIncompleteRequiredField(reasons: ConfirmGatingReasons): boolean {
  return (
    reasons.globalRangeIncomplete ||
    reasons.periodLabelIncomplete ||
    reasons.supplierIncomplete ||
    reasons.monthPeriodIncomplete ||
    reasons.dateRangeIncomplete ||
    reasons.allRowsUnmatched
  );
}

/**
 * The previously-known `defaultSupplierName` to carry over across a
 * "Wyczyść mapowanie" reset (CU-86cbafj4j), or `undefined` when there is
 * nothing to carry. Every resolver spreads `defaultSupplierName: null` from
 * the shared skeleton (`profile-skeleton.ts`) — no resolver can infer which
 * company a file belongs to from column headers alone, only a remembered
 * mapping or manual operator input can set it. The reset re-runs COLUMN
 * recognition only, so a fresh response whose own `defaultSupplierName` is
 * null should still let the operator recover the already-known supplier.
 *
 * Unlike the prior implementation, this value is NOT merged into the fresh
 * profile before `PreviewPhaseEditor` mounts — doing so silently seeded
 * `useRematchedRows`' debounce baseline (`initialKeyRef` in
 * `use-rematched-rows.ts`) with the carried-over supplier already applied,
 * so it could never detect a "change" and never fired `POST /rematch`
 * against the server's actually-unmatched rows (CU-86cbafj4j regression).
 * Instead, `PreviewPhaseEditor` applies this as a POST-MOUNT edit via
 * `editor.setSupplierName`, the same path a manual "Dostawca" pick takes,
 * so the debounce hook sees a real change relative to the true baseline.
 */
export function resolveCarryOverSupplierName(
  freshResponse: AvailabilityImportPreviewResponse,
  previousProfile: AvailabilityProfile | undefined,
): string | undefined {
  if (freshResponse.profile.defaultSupplierName !== null) return undefined;
  return previousProfile?.defaultSupplierName ?? undefined;
}

/** `undefined` (component's own `noDateFallback` label handles it) when no
 * saved-mapping date is known, else the canonically formatted display date. */
export function resolveLastImportedAtLabel(mappingSavedAt: string | null): string | undefined {
  return mappingSavedAt ? formatDisplayDate(mappingSavedAt) : undefined;
}
